/**
 * dealHeaderSummary — the four derived values on the deal detail header card
 * (feedback #70, wig-broker-deals#265).
 *
 * The header used to read `lessor_assignor` / `county` / `state` /
 * `effective_date` off the deal row. Those are legacy flat-tracker columns the
 * current deal record does not have, so every deal showed four blank boxes.
 * The values now come from the deal's own child records, as the land team
 * decided:
 *
 * - Seller: every SELLER entity on the deal (Entities tab), in entity order.
 * - County / State: every distinct value across the deal's lease/deed tracts
 *   (not well tracts), sorted.
 * - Effective Date: an assignment deal's `assignment_effective_date`; any
 *   other deal type uses its lease/deeds' `effective_date` — the earliest
 *   when there is more than one.
 *
 * Pure: callers load the rows (see hooks/useDealHeaderSummary) and pass them
 * in, so the rules are testable without a network.
 */

import { isAssignmentDeal } from "./assignmentOnlyFields";

const SELLER = "SELLER";

const isBlank = (v) => v == null || String(v).trim() === "";

/** Distinct, trimmed, non-blank values in first-seen order. */
const distinct = (values) => {
  const seen = new Set();
  const out = [];
  for (const v of values) {
    if (isBlank(v)) continue;
    const s = String(v).trim();
    if (!seen.has(s)) {
      seen.add(s);
      out.push(s);
    }
  }
  return out;
};

const joinOrNull = (values) => (values.length ? values.join(", ") : null);

/**
 * Every SELLER on the deal, resolved to the entity name. An id the name map
 * cannot resolve shows as `Entity <id>` — the Entities tab's own fallback —
 * never as the row's contact person, who is not the seller. Null until the
 * name map has loaded, so the box shows "—" rather than flashing ids.
 */
export const sellerNames = (dealEntities, entityNames) =>
  entityNames == null
    ? null
    : joinOrNull(
    distinct(
      (Array.isArray(dealEntities) ? dealEntities : [])
        .filter((e) => String(e?.entity_type ?? "").toUpperCase() === SELLER)
        .map((e) =>
          e?.entity_id == null
            ? null
            : (entityNames ?? {})[e.entity_id] ?? `Entity ${e.entity_id}`
        )
    )
  );

/** Distinct values of one tract column, sorted, comma-joined. */
export const tractValues = (tracts, field) =>
  joinOrNull(
    distinct((Array.isArray(tracts) ? tracts : []).map((t) => t?.[field])).sort(
      (a, b) => a.localeCompare(b)
    )
  );

/**
 * The header's effective date as a raw ISO date string (the caller formats
 * it). Assignment: the deal's own date. Otherwise: the earliest lease/deed
 * effective date.
 */
export const effectiveDate = (deal, leaseDeeds) => {
  if (isAssignmentDeal(deal)) {
    return isBlank(deal?.assignment_effective_date)
      ? null
      : deal.assignment_effective_date;
  }
  const dates = (Array.isArray(leaseDeeds) ? leaseDeeds : [])
    .map((ld) => ld?.effective_date)
    .filter((d) => !isBlank(d))
    .map(String)
    .sort(); // ISO yyyy-mm-dd sorts chronologically as text
  return dates.length ? dates[0] : null;
};

/**
 * All four header values at once.
 *
 * @returns {{ seller: string|null, county: string|null, state: string|null,
 *   effectiveDate: string|null }}
 */
export const dealHeaderSummary = ({
  deal,
  dealEntities,
  entityNames,
  leaseDeeds,
  tracts,
}) => ({
  seller: sellerNames(dealEntities, entityNames),
  county: tractValues(tracts, "county"),
  state: tractValues(tracts, "state"),
  effectiveDate: effectiveDate(deal, leaseDeeds),
});
